import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCartStore from '../../context/cartStore';
import { localized } from '../../utils/localized';
import toast from 'react-hot-toast';

const ProductCard = ({ product }) => {
  const { addItem } = useCartStore();
  const [adding, setAdding] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const basePrice = parseFloat(product.base_price) || 0;
  const comparePrice = parseFloat(product.compare_price) || 0;
  const hasDiscount = comparePrice > basePrice && basePrice > 0;
  const discount = hasDiscount ? Math.round((1 - basePrice / comparePrice) * 100) : null;

  const displayName = localized(product, 'name', lang) || product.name;
  const displayCategory = product.category_name
    ? (lang === 'ar' && product.category_name_ar ? product.category_name_ar : product.category_name)
    : '';

  // Stock: 0 = out, 1–5 = low, >5 = normal
  const stock = Number.isFinite(parseInt(product.stock_quantity, 10))
    ? parseInt(product.stock_quantity, 10)
    : null;
  const isOutOfStock = stock !== null && stock <= 0;
  const isLowStock   = stock !== null && stock > 0 && stock <= 5;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    if (isOutOfStock) return;
    setAdding(true);
    addItem(product);
    toast.success(`${displayName} ${t('product.added')}`, { icon: '🍯' });
    setTimeout(() => setAdding(false), 600);
  };

  return (
    <Link to={`/product/${product.slug}`} className="product-card">
      <div className="product-card__img-wrap">
        {product.primary_image
          ? <img src={product.primary_image} alt={displayName} loading="lazy" />
          : <div className="product-card__placeholder">🍯</div>
        }
        {product.is_bestseller && <span className="product-card__tag">{t('product.bestseller')}</span>}
        {discount && <span className="product-card__discount">-{discount}%</span>}
        {isOutOfStock && <span className="product-card__sold-out">{t('product.outOfStock')}</span>}
        <button
          className="product-card__quick-add"
          onClick={handleQuickAdd}
          disabled={adding || isOutOfStock}
        >
          {isOutOfStock ? t('product.outOfStock') : (adding ? '✓' : t('product.addToBasket'))}
        </button>
      </div>

      <div className="product-card__body">
        {displayCategory && <p className="product-card__category">{displayCategory}</p>}
        <h3 className="product-card__name">{displayName}</h3>
        {product.origin_country && (
          <p className="product-card__origin">🌍 {product.origin_country}</p>
        )}
        <div className="product-card__price-row">
          <span className="product-card__price">KD {basePrice.toFixed(3)}</span>
          {hasDiscount && (
            <span className="product-card__compare">KD {comparePrice.toFixed(3)}</span>
          )}
        </div>
        {product.avg_rating > 0 && (
          <div className="product-card__stars">
            {'★'.repeat(Math.round(product.avg_rating))}{'☆'.repeat(5 - Math.round(product.avg_rating))}
            <span>({product.review_count})</span>
          </div>
        )}
        {stock !== null && (
          <p className={`product-card__stock ${isOutOfStock ? 'out' : isLowStock ? 'low' : 'ok'}`}>
            {isOutOfStock
              ? t('product.outOfStock')
              : isLowStock
                ? t('product.lowStock', { count: stock })
                : t('product.stockCount', { count: stock })}
          </p>
        )}
      </div>

      <style>{`
        .product-card {
          display: block; background: var(--white); border-radius: var(--radius-lg);
          overflow: hidden; transition: all 0.3s ease;
          box-shadow: 0 2px 12px rgba(44,36,22,0.06);
        }
        .product-card:hover { box-shadow: var(--shadow-md); transform: translateY(-6px); }
        .product-card__img-wrap {
          position: relative; aspect-ratio: 1; overflow: hidden; background: var(--cream);
        }
        .product-card__img-wrap img {
          width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease;
        }
        .product-card:hover .product-card__img-wrap img { transform: scale(1.06); }
        .product-card__placeholder {
          width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 4rem;
        }
        .product-card__tag {
          position: absolute; top: 12px; left: 12px; background: var(--gold); color: white;
          padding: 4px 12px; border-radius: 50px; font-size: 0.72rem; font-weight: 700;
        }
        [dir="rtl"] .product-card__tag { left: auto; right: 12px; }
        .product-card__discount {
          position: absolute; top: 12px; right: 12px; background: #e53e3e; color: white;
          padding: 4px 10px; border-radius: 50px; font-size: 0.72rem; font-weight: 700;
        }
        [dir="rtl"] .product-card__discount { right: auto; left: 12px; }
        .product-card__quick-add {
          position: absolute; bottom: 0; left: 0; right: 0; background: var(--olive);
          color: white; padding: 12px; font-size: 0.85rem; font-weight: 600; font-family: var(--font-sans);
          transform: translateY(100%); transition: transform 0.25s ease;
        }
        .product-card:hover .product-card__quick-add { transform: translateY(0); }
        .product-card__body { padding: 16px; }
        .product-card__category { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 1px; color: var(--gold); font-weight: 600; margin-bottom: 4px; }
        .product-card__name { font-family: var(--font-serif); font-size: 1.05rem; font-weight: 500; margin-bottom: 4px; }
        .product-card__origin { font-size: 0.75rem; color: var(--text-muted); margin-bottom: 10px; }
        .product-card__price-row { display: flex; align-items: center; gap: 10px; }
        .product-card__price { font-weight: 700; font-size: 1rem; color: var(--text); }
        .product-card__compare { font-size: 0.85rem; color: var(--text-muted); text-decoration: line-through; }
        .product-card__stars { font-size: 0.75rem; color: var(--gold); margin-top: 6px; }
        .product-card__stars span { color: var(--text-muted); margin-left: 4px; }
        [dir="rtl"] .product-card__stars span { margin-left: 0; margin-right: 4px; }
        .product-card__stock { font-size: 0.75rem; margin-top: 8px; font-weight: 600; }
        .product-card__stock.ok  { color: var(--text-muted); font-weight: 500; }
        .product-card__stock.low { color: #d97706; }
        .product-card__stock.out { color: #dc2626; }
        .product-card__sold-out {
          position: absolute; top: 12px; right: 12px; background: #dc2626; color: white;
          padding: 4px 10px; border-radius: 50px; font-size: 0.72rem; font-weight: 700;
        }
        [dir="rtl"] .product-card__sold-out { right: auto; left: 12px; }
        .product-card__quick-add:disabled { opacity: 0.7; cursor: not-allowed; background: #888; }
      `}</style>
    </Link>
  );
};

export default ProductCard;
